import { deflate } from "pako"
import type { MmaCsvRow } from "./parseMmaCsv"

// TIFF type codes
const SHORT = 3
const LONG = 4
const RATIONAL = 5
const ASCII = 2
const BYTE = 1

function p2(n: number) {
    return n % 2 === 0 ? n : n + 1
}

function asciiBytes(s: string): Uint8Array {
    const nullTerminated = s + "\0"
    return new TextEncoder().encode(nullTerminated)
}

/** Convert decimal degrees to [deg, min, sec] as rational pairs [[n,d],[n,d],[n,d]] */
function toDms(deg: number): [[number, number], [number, number], [number, number]] {
    const abs = Math.abs(deg)
    const d = Math.floor(abs)
    const mFull = (abs - d) * 60
    const m = Math.floor(mFull)
    const sFull = (mFull - m) * 60
    const sNum = Math.round(sFull * 10000)
    return [
        [d, 1],
        [m, 1],
        [sNum, 10000],
    ]
}

function microsToExifDateTime(micros: number): string {
    const d = new Date(micros / 1000)
    const pad = (n: number) => String(n).padStart(2, "0")
    return (
        `${d.getUTCFullYear()}:${pad(d.getUTCMonth() + 1)}:${pad(d.getUTCDate())} ` +
        `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
    )
}

/**
 * Builds the XMP packet. The DJI DewarpData / gimbal block and altitude are
 * band-agnostic and always emitted. The FLIR thermal block is only emitted for
 * thermal imagery — visible (RGB) and NIR frames must not claim to be LWIR.
 */
function buildXmp(row: MmaCsvRow, thermal: boolean): Uint8Array {
    // Pixel-space focal lengths derived from the per-frame FOV, used for DewarpData.
    const fxPx = row.hFov > 0 ? row.imageWidth / 2 / Math.tan(((row.hFov / 2) * Math.PI) / 180) : 0
    const fyPx = row.vFov > 0 ? row.imageHeight / 2 / Math.tan(((row.vFov / 2) * Math.PI) / 180) : 0
    // DJI DewarpData format: date;fx,fy,cx_offset,cy_offset,k1,k2,p1,p2,k3
    // cx/cy offsets and tangential terms are unknown; k3 is not provided by MMA.
    const dewarpData =
        fxPx > 0 && fyPx > 0
            ? `1970-01-01;${fxPx.toFixed(2)},${fyPx.toFixed(2)},0.0,0.0,${row.radialDistortionK1},${row.radialDistortionK2},0.0,0.0,0.0`
            : null

    const xml = [
        '<?xpacket begin="﻿" id="W5M0MpCehiHzreSzNTczkc9d"?>',
        '<x:xmpmeta xmlns:x="adobe:ns:meta/">',
        '<rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">',
        `<rdf:Description rdf:about="" xmlns:drone-dji="http://www.dji.com/drone-dji/1.0/">`,
        `<drone-dji:AbsoluteAltitude>${row.sensorAltitudeMsl.toFixed(3)}</drone-dji:AbsoluteAltitude>`,
        `<drone-dji:RelativeAltitude>${row.sensorAltitudeAgl.toFixed(3)}</drone-dji:RelativeAltitude>`,
        `<drone-dji:GimbalRollDegree>${row.sensorRoll.toFixed(4)}</drone-dji:GimbalRollDegree>`,
        `<drone-dji:GimbalYawDegree>${row.sensorYaw.toFixed(4)}</drone-dji:GimbalYawDegree>`,
        `<drone-dji:GimbalPitchDegree>${row.sensorPitch.toFixed(4)}</drone-dji:GimbalPitchDegree>`,
        dewarpData ? `<drone-dji:DewarpData>${dewarpData}</drone-dji:DewarpData>` : null,
        "</rdf:Description>",
        thermal
            ? `<rdf:Description rdf:about="" xmlns:FLIR="http://www.flir.com/flir/1.0/">`
            : null,
        thermal ? `<FLIR:TlinearGain>${row.gain.toFixed(3)}</FLIR:TlinearGain>` : null,
        thermal ? `<FLIR:BandName>LWIR</FLIR:BandName>` : null,
        thermal ? `<FLIR:CentralWavelength>10000</FLIR:CentralWavelength>` : null,
        thermal ? `<FLIR:WavelengthFWHM>4500</FLIR:WavelengthFWHM>` : null,
        thermal ? "</rdf:Description>" : null,
        "</rdf:RDF>",
        "</x:xmpmeta>",
        '<?xpacket end="w"?>',
    ]
        .filter(Boolean)
        .join("\n")
    return new TextEncoder().encode(xml)
}

/** Builds a GPS sub-IFD as a self-contained byte array. */
function buildGpsIfd(row: MmaCsvRow, baseOffset: number): Uint8Array {
    const lat = row.sensorLatitude
    const lon = row.sensorLongitude
    const alt = row.sensorAltitudeWgs84

    const latDms = toDms(lat)
    const lonDms = toDms(lon)
    const datum = asciiBytes("WGS-84")

    // GPS IFD has 8 tags
    const N = 8
    const coreSize = 2 + N * 12 + 4

    // LatRef and LonRef are ASCII[2] — TIFF requires values ≤ 4 bytes to be
    // stored inline in the tag's value field, not as offset pointers.
    let ov = 0
    const latOff = ov
    ov += 24 // 3 RATIONALs × 8B
    const lonOff = ov
    ov += 24 // 3 RATIONALs × 8B
    const altOff = ov
    ov += 8 // 1 RATIONAL × 8B
    const datumOff = ov
    ov += p2(datum.length)

    const buf = new Uint8Array(coreSize + ov)
    const view = new DataView(buf.buffer)
    const ovBase = baseOffset + coreSize

    let pos = 0
    view.setUint16(pos, N, true)
    pos += 2

    const writeOffsetTag = (id: number, type: number, count: number, offset: number) => {
        view.setUint16(pos, id, true)
        view.setUint16(pos + 2, type, true)
        view.setUint32(pos + 4, count, true)
        view.setUint32(pos + 8, offset, true)
        pos += 12
    }

    // GPSVersionID — 4 bytes inline
    view.setUint16(pos, 0x0000, true)
    view.setUint16(pos + 2, BYTE, true)
    view.setUint32(pos + 4, 4, true)
    buf[pos + 8] = 2
    buf[pos + 9] = 3
    buf[pos + 10] = 0
    buf[pos + 11] = 0
    pos += 12

    // GPSLatitudeRef — ASCII[2] inline ("N\0" or "S\0")
    view.setUint16(pos, 0x0001, true)
    view.setUint16(pos + 2, ASCII, true)
    view.setUint32(pos + 4, 2, true)
    buf[pos + 8] = lat >= 0 ? 78 : 83 // 'N' or 'S'
    buf[pos + 9] = 0
    pos += 12

    writeOffsetTag(0x0002, RATIONAL, 3, ovBase + latOff)

    // GPSLongitudeRef — ASCII[2] inline ("E\0" or "W\0")
    view.setUint16(pos, 0x0003, true)
    view.setUint16(pos + 2, ASCII, true)
    view.setUint32(pos + 4, 2, true)
    buf[pos + 8] = lon >= 0 ? 69 : 87 // 'E' or 'W'
    buf[pos + 9] = 0
    pos += 12

    writeOffsetTag(0x0004, RATIONAL, 3, ovBase + lonOff)

    // GPSAltitudeRef — 1 byte inline (0 = above sea level)
    view.setUint16(pos, 0x0005, true)
    view.setUint16(pos + 2, BYTE, true)
    view.setUint32(pos + 4, 1, true)
    view.setUint32(pos + 8, 0, true)
    pos += 12

    writeOffsetTag(0x0006, RATIONAL, 1, ovBase + altOff)
    writeOffsetTag(0x0012, ASCII, datum.length, ovBase + datumOff)

    view.setUint32(pos, 0, true)
    pos += 4 // next IFD

    // Overflow: DMS rationals and datum string
    const writeRational = (offset: number, n: number, d: number) => {
        view.setUint32(coreSize + offset, n, true)
        view.setUint32(coreSize + offset + 4, d, true)
    }
    const writeRationals3 = (
        offset: number,
        dms: [[number, number], [number, number], [number, number]]
    ) => {
        writeRational(offset, dms[0][0], dms[0][1])
        writeRational(offset + 8, dms[1][0], dms[1][1])
        writeRational(offset + 16, dms[2][0], dms[2][1])
    }

    writeRationals3(latOff, latDms)
    writeRationals3(lonOff, lonDms)
    writeRational(altOff, Math.round(Math.abs(alt) * 100), 100)
    buf.set(datum, coreSize + datumOff)

    return buf
}

/** Builds an Exif sub-IFD. */
function buildExifIfd(row: MmaCsvRow, baseOffset: number): Uint8Array {
    const dtOrig = asciiBytes(microsToExifDateTime(row.timeMicros))
    const focalNum = Math.round(row.focalLengthMm * 100)

    // 35mm-equivalent focal length from the per-frame diagonal FOV.
    // Lets sensortool's get_fov_from_35mm_focal_length() recover the calibrated
    // FOV instead of falling back to the hardcoded model table in overwatch.rs.
    const diag35mmHalf = Math.sqrt(18 * 18 + 12 * 12)
    const hHalf = ((row.hFov / 2) * Math.PI) / 180
    const vHalf = ((row.vFov / 2) * Math.PI) / 180
    const dfovHalf = Math.atan(Math.sqrt(Math.tan(hHalf) ** 2 + Math.tan(vHalf) ** 2))
    const fl35 = row.hFov > 0 && row.vFov > 0 ? Math.round(diag35mmHalf / Math.tan(dfovHalf)) : 0

    const N = 3
    const coreSize = 2 + N * 12 + 4

    let ov = 0
    const dtOrigOff = ov
    ov += p2(dtOrig.length)
    const focalOff = ov
    ov += 8

    const buf = new Uint8Array(coreSize + ov)
    const view = new DataView(buf.buffer)
    const ovBase = baseOffset + coreSize

    let pos = 0
    view.setUint16(pos, N, true)
    pos += 2

    // 0x9003 DateTimeOriginal
    view.setUint16(pos, 0x9003, true)
    view.setUint16(pos + 2, ASCII, true)
    view.setUint32(pos + 4, dtOrig.length, true)
    view.setUint32(pos + 8, ovBase + dtOrigOff, true)
    pos += 12

    // 0x920a FocalLength (rational, mm × 100)
    view.setUint16(pos, 0x920a, true)
    view.setUint16(pos + 2, RATIONAL, true)
    view.setUint32(pos + 4, 1, true)
    view.setUint32(pos + 8, ovBase + focalOff, true)
    pos += 12

    // 0xa405 FocalLengthIn35mmFormat (SHORT, inline)
    view.setUint16(pos, 0xa405, true)
    view.setUint16(pos + 2, SHORT, true)
    view.setUint32(pos + 4, 1, true)
    view.setUint16(pos + 8, fl35, true)
    pos += 12

    view.setUint32(pos, 0, true) // next IFD

    buf.set(dtOrig, coreSize + dtOrigOff)
    view.setUint32(coreSize + focalOff, focalNum, true)
    view.setUint32(coreSize + focalOff + 4, 100, true)

    return buf
}

export type EncodeTiffOptions = {
    /** 1 = 16-bit grayscale (default), 3 = 8-bit RGB. */
    channels?: 1 | 3
    /** Emit the FLIR/LWIR thermal XMP block and thermal tags. Defaults to true. */
    thermal?: boolean
}

/**
 * Encodes raster pixels as a TIFF with GPS, EXIF, and XMP metadata extracted from
 * an MMA telemetry CSV row.
 *
 * - channels=1 (default): 16-bit grayscale. `pixels` is big-endian (from PNG) or
 *   native 16-bit samples (from .owi). Output is little-endian ("II"). With
 *   thermal=true this reproduces the original MMA thermal converter byte-for-byte.
 * - channels=3: 8-bit RGB. `pixels` is interleaved RGB 16-bit samples (length
 *   w*h*3, from the .owi demosaic); the high byte of each sample is written.
 */
export function encodeTiff(
    pixels: Uint16Array,
    width: number,
    height: number,
    row: MmaCsvRow,
    options?: EncodeTiffOptions
): ArrayBuffer {
    const channels = options?.channels ?? 1
    const thermal = options?.thermal ?? true
    return channels === 3
        ? encodeRgb8(pixels, width, height, row)
        : encodeMono16(pixels, width, height, row, thermal)
}

function encodeMono16(
    pixels: Uint16Array,
    width: number,
    height: number,
    row: MmaCsvRow,
    thermal: boolean
): ArrayBuffer {
    // Convert big-endian 16-bit pixels to little-endian TIFF bytes
    const leBytes = new Uint8Array(pixels.length * 2)
    for (let i = 0; i < pixels.length; i++) {
        leBytes[i * 2 + 0] = pixels[i] & 0xff
        leBytes[i * 2 + 1] = (pixels[i] >> 8) & 0xff
    }
    const raster = deflate(leBytes)

    // Build variable pieces
    const makeBytes = asciiBytes(row.cameraMake)
    const modelBytes = asciiBytes(row.cameraModel)
    const dtBytes = asciiBytes(microsToExifDateTime(row.timeMicros))
    const softBytes = asciiBytes("nova-mma-converter")
    const xmpBytes = buildXmp(row, thermal)

    // IFD0: 20 tags
    const N0 = 20
    const ifd0CoreSize = 2 + N0 * 12 + 4
    const ifd0Base = 8 // right after TIFF header

    // IFD0 overflow layout (offsets relative to overflow start)
    let ov = 0
    const makeOff = ov
    ov += p2(makeBytes.length)
    const modelOff = ov
    ov += p2(modelBytes.length)
    const dtOff = ov
    ov += p2(dtBytes.length)
    const softOff = ov
    ov += p2(softBytes.length)
    const xresOff = ov
    ov += 8
    const yresOff = ov
    ov += 8
    const xmpOff = ov
    ov += p2(xmpBytes.length)
    const ifd0OverflowSize = ov

    // Sub-IFDs are located after IFD0 overflow
    const overflowBase = ifd0Base + ifd0CoreSize
    const gpsAbsBase = overflowBase + ifd0OverflowSize
    const gpsIfd = buildGpsIfd(row, gpsAbsBase)
    const exifAbsBase = gpsAbsBase + p2(gpsIfd.length)
    const exifIfd = buildExifIfd(row, exifAbsBase)
    const rasterOffset = exifAbsBase + p2(exifIfd.length)

    const totalSize = rasterOffset + raster.length
    const out = new Uint8Array(totalSize)
    const view = new DataView(out.buffer)

    // TIFF header
    out[0] = 0x49
    out[1] = 0x49 // "II" little-endian
    view.setUint16(2, 42, true)
    view.setUint32(4, ifd0Base, true)

    // IFD0 core
    let pos = ifd0Base
    view.setUint16(pos, N0, true)
    pos += 2

    const writeInlineShort = (id: number, v: number) => {
        view.setUint16(pos, id, true)
        view.setUint16(pos + 2, SHORT, true)
        view.setUint32(pos + 4, 1, true)
        view.setUint16(pos + 8, v, true)
        pos += 12
    }
    const writeInlineLong = (id: number, v: number) => {
        view.setUint16(pos, id, true)
        view.setUint16(pos + 2, LONG, true)
        view.setUint32(pos + 4, 1, true)
        view.setUint32(pos + 8, v, true)
        pos += 12
    }
    const writeOffsetTag = (id: number, type: number, count: number, offset: number) => {
        view.setUint16(pos, id, true)
        view.setUint16(pos + 2, type, true)
        view.setUint32(pos + 4, count, true)
        view.setUint32(pos + 8, offset, true)
        pos += 12
    }

    // Tags must be in ascending ID order
    writeInlineLong(0x00fe, 0) // NewSubfileType
    writeInlineLong(0x0100, width) // ImageWidth
    writeInlineLong(0x0101, height) // ImageLength
    writeInlineShort(0x0102, 16) // BitsPerSample
    writeInlineShort(0x0103, 8) // Compression (Deflate/Adobe)
    writeInlineShort(0x0106, 1) // PhotometricInterpretation
    writeOffsetTag(0x010f, ASCII, makeBytes.length, overflowBase + makeOff) // Make
    writeOffsetTag(0x0110, ASCII, modelBytes.length, overflowBase + modelOff) // Model
    writeInlineLong(0x0111, rasterOffset) // StripOffsets
    writeInlineShort(0x0115, 1) // SamplesPerPixel
    writeInlineLong(0x0116, height) // RowsPerStrip
    writeInlineLong(0x0117, raster.length) // StripByteCounts
    writeOffsetTag(0x011a, RATIONAL, 1, overflowBase + xresOff) // XResolution
    writeOffsetTag(0x011b, RATIONAL, 1, overflowBase + yresOff) // YResolution
    writeInlineShort(0x0128, 2) // ResolutionUnit (inch)
    writeOffsetTag(0x0131, ASCII, softBytes.length, overflowBase + softOff) // Software
    writeOffsetTag(0x0132, ASCII, dtBytes.length, overflowBase + dtOff) // DateTime
    writeOffsetTag(0x02bc, BYTE, xmpBytes.length, overflowBase + xmpOff) // ApplicationNotes (XMP)
    writeInlineLong(0x8769, exifAbsBase) // ExifIFD pointer
    writeInlineLong(0x8825, gpsAbsBase) // GPSInfoIFD pointer

    view.setUint32(pos, 0, true) // next IFD pointer

    // IFD0 overflow data
    out.set(makeBytes, overflowBase + makeOff)
    out.set(modelBytes, overflowBase + modelOff)
    out.set(dtBytes, overflowBase + dtOff)
    out.set(softBytes, overflowBase + softOff)
    view.setUint32(overflowBase + xresOff, 72, true)
    view.setUint32(overflowBase + xresOff + 4, 1, true)
    view.setUint32(overflowBase + yresOff, 72, true)
    view.setUint32(overflowBase + yresOff + 4, 1, true)
    out.set(xmpBytes, overflowBase + xmpOff)

    // Sub-IFDs
    out.set(gpsIfd, gpsAbsBase)
    out.set(exifIfd, exifAbsBase)

    // Compressed raster
    out.set(raster, rasterOffset)

    return out.buffer
}

function encodeRgb8(
    pixels: Uint16Array,
    width: number,
    height: number,
    row: MmaCsvRow
): ArrayBuffer {
    // Downconvert interleaved 16-bit RGB samples to 8-bit (high byte) and deflate.
    const rgbBytes = new Uint8Array(width * height * 3)
    for (let i = 0; i < rgbBytes.length; i++) {
        rgbBytes[i] = (pixels[i] >> 8) & 0xff
    }
    const raster = deflate(rgbBytes)

    // Build variable pieces (visible imagery — never thermal)
    const makeBytes = asciiBytes(row.cameraMake)
    const modelBytes = asciiBytes(row.cameraModel)
    const dtBytes = asciiBytes(microsToExifDateTime(row.timeMicros))
    const softBytes = asciiBytes("nova-mma-converter")
    const xmpBytes = buildXmp(row, false)

    // IFD0: 21 tags (BitsPerSample is a 3-value offset tag; PlanarConfiguration added)
    const N0 = 21
    const ifd0CoreSize = 2 + N0 * 12 + 4
    const ifd0Base = 8

    let ov = 0
    const bpsOff = ov
    ov += 6 // 3 × SHORT
    const makeOff = ov
    ov += p2(makeBytes.length)
    const modelOff = ov
    ov += p2(modelBytes.length)
    const dtOff = ov
    ov += p2(dtBytes.length)
    const softOff = ov
    ov += p2(softBytes.length)
    const xresOff = ov
    ov += 8
    const yresOff = ov
    ov += 8
    const xmpOff = ov
    ov += p2(xmpBytes.length)
    const ifd0OverflowSize = ov

    const overflowBase = ifd0Base + ifd0CoreSize
    const gpsAbsBase = overflowBase + ifd0OverflowSize
    const gpsIfd = buildGpsIfd(row, gpsAbsBase)
    const exifAbsBase = gpsAbsBase + p2(gpsIfd.length)
    const exifIfd = buildExifIfd(row, exifAbsBase)
    const rasterOffset = exifAbsBase + p2(exifIfd.length)

    const totalSize = rasterOffset + raster.length
    const out = new Uint8Array(totalSize)
    const view = new DataView(out.buffer)

    out[0] = 0x49
    out[1] = 0x49
    view.setUint16(2, 42, true)
    view.setUint32(4, ifd0Base, true)

    let pos = ifd0Base
    view.setUint16(pos, N0, true)
    pos += 2

    const writeInlineShort = (id: number, v: number) => {
        view.setUint16(pos, id, true)
        view.setUint16(pos + 2, SHORT, true)
        view.setUint32(pos + 4, 1, true)
        view.setUint16(pos + 8, v, true)
        pos += 12
    }
    const writeInlineLong = (id: number, v: number) => {
        view.setUint16(pos, id, true)
        view.setUint16(pos + 2, LONG, true)
        view.setUint32(pos + 4, 1, true)
        view.setUint32(pos + 8, v, true)
        pos += 12
    }
    const writeOffsetTag = (id: number, type: number, count: number, offset: number) => {
        view.setUint16(pos, id, true)
        view.setUint16(pos + 2, type, true)
        view.setUint32(pos + 4, count, true)
        view.setUint32(pos + 8, offset, true)
        pos += 12
    }

    // Tags must be in ascending ID order
    writeInlineLong(0x00fe, 0) // NewSubfileType
    writeInlineLong(0x0100, width) // ImageWidth
    writeInlineLong(0x0101, height) // ImageLength
    writeOffsetTag(0x0102, SHORT, 3, overflowBase + bpsOff) // BitsPerSample [8,8,8]
    writeInlineShort(0x0103, 8) // Compression (Deflate/Adobe)
    writeInlineShort(0x0106, 2) // PhotometricInterpretation (RGB)
    writeOffsetTag(0x010f, ASCII, makeBytes.length, overflowBase + makeOff) // Make
    writeOffsetTag(0x0110, ASCII, modelBytes.length, overflowBase + modelOff) // Model
    writeInlineLong(0x0111, rasterOffset) // StripOffsets
    writeInlineShort(0x0115, 3) // SamplesPerPixel
    writeInlineLong(0x0116, height) // RowsPerStrip
    writeInlineLong(0x0117, raster.length) // StripByteCounts
    writeOffsetTag(0x011a, RATIONAL, 1, overflowBase + xresOff) // XResolution
    writeOffsetTag(0x011b, RATIONAL, 1, overflowBase + yresOff) // YResolution
    writeInlineShort(0x011c, 1) // PlanarConfiguration (chunky)
    writeInlineShort(0x0128, 2) // ResolutionUnit (inch)
    writeOffsetTag(0x0131, ASCII, softBytes.length, overflowBase + softOff) // Software
    writeOffsetTag(0x0132, ASCII, dtBytes.length, overflowBase + dtOff) // DateTime
    writeOffsetTag(0x02bc, BYTE, xmpBytes.length, overflowBase + xmpOff) // ApplicationNotes (XMP)
    writeInlineLong(0x8769, exifAbsBase) // ExifIFD pointer
    writeInlineLong(0x8825, gpsAbsBase) // GPSInfoIFD pointer

    view.setUint32(pos, 0, true) // next IFD pointer

    // IFD0 overflow data
    view.setUint16(overflowBase + bpsOff, 8, true)
    view.setUint16(overflowBase + bpsOff + 2, 8, true)
    view.setUint16(overflowBase + bpsOff + 4, 8, true)
    out.set(makeBytes, overflowBase + makeOff)
    out.set(modelBytes, overflowBase + modelOff)
    out.set(dtBytes, overflowBase + dtOff)
    out.set(softBytes, overflowBase + softOff)
    view.setUint32(overflowBase + xresOff, 72, true)
    view.setUint32(overflowBase + xresOff + 4, 1, true)
    view.setUint32(overflowBase + yresOff, 72, true)
    view.setUint32(overflowBase + yresOff + 4, 1, true)
    out.set(xmpBytes, overflowBase + xmpOff)

    out.set(gpsIfd, gpsAbsBase)
    out.set(exifIfd, exifAbsBase)
    out.set(raster, rasterOffset)

    return out.buffer
}
